import {
  ChangeDetectorRef, Component, forwardRef,
  Inject, Input, NgZone, OnDestroy, PLATFORM_ID
} from '@angular/core';
import {DashboardBaseChart} from "../shared/dashboard-base-chart";
import {DashboardBaseComponent} from "../shared/dashboard-base-component";
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5Local from '@amcharts/amcharts5/locales/es_ES';
import {isPlatformBrowser} from "@angular/common";
import {ChartLoadingComponent} from "../chart-loading/chart-loading.component";


export interface ChartBarSeries {
  field: string;
  name: string;
  color: string;
  colorField?: string;                          // campo del dato que sobreescribe el color por fila
  showValueLabel?: boolean;                      // etiqueta de valor sobre la barra (default false)
  valueLabelFormatter?: (v: number) => string;   // formatea la etiqueta y el tooltip de esta serie
}

export interface ChartBarConfig {
  chartId: string;
  categoryField: string;
  bars: ChartBarSeries[];                        // una entrada = 1 serie; varias = barras agrupadas
  stacked?: boolean;                             // barras apiladas
  showLegend?: boolean;                          // default: se muestra si hay más de 1 serie
  xLabelFormatter?: (value: number) => string;   // formatea los labels del eje X (valor)
  tooltipValueFormatter?: (value: number) => string; // fallback si el bar no define valueLabelFormatter
  barHeight?: number;                            // alto en px por fila de categoría (se multiplica por cant. de bars si no es apilado)
  maxHeight?: number;                            // alto máx. del wrapper con scroll
  data: any[];
}

/** Esto es para armar un card, no hace ningun efecto en el componente */
export interface ChartBarCardConfig {
  title: string;
  sub?: string;
  lastUpdated?: string;
  chart: ChartBarConfig;
}

@Component({
  selector: 'app-chart-bar',
  templateUrl: './chart-bar.component.html',
  standalone: true,
  styleUrls: ['./chart-bar.component.scss'],
  imports: [
    ChartLoadingComponent
  ],
  providers: [
    {
      provide: DashboardBaseComponent,
      useExisting: forwardRef(() => ChartBarComponent)
    }
  ]
})
export class ChartBarComponent extends DashboardBaseChart implements OnDestroy {

  @Input() config!: ChartBarConfig;

  isLoading = true;

  private yAxis?: am5xy.CategoryAxis<am5xy.AxisRendererY>;
  private barSeriesList: am5xy.ColumnSeries[] = [];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone, private cdr: ChangeDetectorRef) {
    super();
  }

   initChart(): void {
    if (!isPlatformBrowser(this.platformId) || !this.config?.chartId) return;

    this.zone.runOutsideAngular(() => {
      const root = am5.Root.new(this.config.chartId);
      root.setThemes([am5themes_Animated.new(root)]);
      if (root._logo) root._logo.dispose();
      root.locale = am5Local;
      this.root = root;

      const textColor = am5.color(0xd9e8ff);
      const mutedColor = am5.color(0x9db8d7);
      const gridColor = am5.color(0x33506f);

      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          layout: root.verticalLayout,
          panX: false,
          panY: false,
          paddingLeft: 0,
        })
      );

      // Y axis — categories
      this.yAxis = chart.yAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: this.config.categoryField,
          renderer: am5xy.AxisRendererY.new(root, {minGridDistance: 18}),
        })
      );
      this.yAxis.get('renderer').labels.template.setAll({
        fill: mutedColor,
        fontSize: 11,
        maxWidth: 150,
        oversizedBehavior: 'fit',
      });
      this.yAxis.get('renderer').grid.template.setAll({stroke: gridColor, strokeOpacity: 0.35});

      // X axis — values
      const xAxis = chart.xAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererX.new(root, {}),
        })
      );
      xAxis.get('renderer').labels.template.setAll({fill: mutedColor, fontSize: 11});
      xAxis.get('renderer').grid.template.setAll({stroke: gridColor, strokeOpacity: 0.35});
      if (this.config.xLabelFormatter) {
        const fmt = this.config.xLabelFormatter;
        xAxis.get('renderer').labels.template.adapters.add('text', (_text, target) => {
          const value = (target.dataItem as any)?.get('value') as number | undefined;
          return value != null ? fmt(value) : _text;
        });
      }

      // Bar series
      for (const bar of this.config.bars) {
        const barColor = am5.color(bar.color);
        const hasPerBarColor = !!bar.colorField;

        const tooltipText = this.config.stacked
          ? `[bold #fff]{categoryY} - ${bar.name}: {valueX}`
          : `[bold #fff]{categoryY}: {valueX}`;
        const tooltip = am5.Tooltip.new(root, {
          labelText: tooltipText,
        });
        const tooltipFmt = bar.valueLabelFormatter ?? this.config.tooltipValueFormatter;
        if (tooltipFmt) {
          tooltip.label.adapters.add('text', (_text, target) => {
            const value = (target.dataItem as any)?.get('valueX') as number | undefined;
            const cat = (target.dataItem as any)?.get('categoryY') as string | undefined;
            const prefix = this.config.stacked ? `${cat ?? ''} - ${bar.name}` : (cat ?? '');
            return value != null ? `[bold #fff]${prefix}: ${tooltipFmt(value)}` : _text;
          });
        }

        const series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: bar.name,
            xAxis,
            yAxis: this.yAxis,
            categoryYField: this.config.categoryField,
            valueXField: bar.field,
            stacked: this.config.stacked,
            sequencedInterpolation: true,
            tooltip,
          })
        );

        series.columns.template.setAll({
          fill: barColor,
          strokeOpacity: 0,
          height: am5.percent(80),
          cornerRadiusTR: this.config.stacked ? 0 : 6,
          cornerRadiusBR: this.config.stacked ? 0 : 6,
        });

        if (hasPerBarColor) {
          series.columns.template.adapters.add('fill', (_fill: any, target: any) => {
            const ctx = target.dataItem?.dataContext as any;
            return ctx?.[bar.colorField!] ? am5.color(ctx[bar.colorField!]) : barColor;
          });
          series.columns.template.adapters.add('stroke', (_stroke: any, target: any) => {
            const ctx = target.dataItem?.dataContext as any;
            return ctx?.[bar.colorField!] ? am5.color(ctx[bar.colorField!]) : barColor;
          });
        }

        if (bar.showValueLabel) {
          const fmt = bar.valueLabelFormatter;
          series.bullets.push(() => {
            const label = am5.Label.new(root, {
              text: `[bold]{${bar.field}}`,
              fill: textColor,
              centerX: this.config.stacked ? am5.p50 : undefined,
              centerY: am5.p50,
              populateText: true,
              fontSize: 10,
              paddingRight: this.config.stacked ? 0 : 5,
            });
            if (this.config.stacked) {
              label.adapters.add('text', (_text, target) => {
                const value = (target.dataItem as any)?.get('valueX') as number | undefined;
                if (!value) return '';
                return fmt ? `[bold]${fmt(value)}` : _text;
              });
            } else if (fmt) {
              label.adapters.add('text', (_text, target) => {
                const value = (target.dataItem as any)?.get('valueX') as number | undefined;
                return value != null ? `[bold]${fmt(value)}` : _text;
              });
            }
            return am5.Bullet.new(root, {
              locationX: this.config.stacked ? 0.5 : 0,
              locationY: 0.5,
              sprite: label
            });
          });
        }

        series.appear(500);
        this.barSeriesList.push(series);
      }

      // Legend (shown by default when there is more than one series)
      const showLegend = this.config.showLegend ?? this.config.bars.length > 1;
      if (showLegend) {
        const legend = chart.children.push(
          am5.Legend.new(root, {
            x: am5.percent(50),
            centerX: am5.percent(50),
          })
        );
        legend.labels.template.setAll({fill: textColor, fontSize: 12});
        legend.valueLabels.template.setAll({forceHidden: true});
        legend.data.setAll(chart.series.values);
      }

      chart.appear(500, 100);
    });
  }

  load(data: any[]): void {
    this.isLoading = false;
    this.cdr.detectChanges();
    if (!this.root) this.initChart();

    this.setDynamicHeight(data.length);
    this.config.data = data;
    const reversed = [...data].reverse();
    this.yAxis?.data.setAll(reversed);
    this.barSeriesList.forEach(s => s.data.setAll(reversed));
    this.isLoaded = true;
  }

  private setDynamicHeight(dataLength: number): void {
    const el = document.getElementById(this.config.chartId);
    if (!el) return;
    const groupSize = this.config.stacked ? 1 : (this.config.bars.length || 1);
    el.style.height = this.config.barHeight
      ? `${dataLength * this.config.barHeight * groupSize}px`
      : '350px';
  }

  ngOnDestroy(): void {
    this.barSeriesList = [];
    this.root?.dispose();
  }

}
